import { FC } from "react";

import { Translate } from "@/app/application/Translate";
import { TrainingKpi } from "@/features/training/model/Training/TrainingKpi";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import {
	Box,
	Chip,
	List,
	ListItem,
	Paper,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

interface KpiParametersProps {
	kpis: TrainingKpi[];
	/** Reduced margins for embedded layouts */
	compact?: boolean;
}

export const KpiParameters: FC<KpiParametersProps> = ({
	kpis,
	compact = false,
}) => {
	const theme = useTheme();

	const formatNumber = (value: number | string | undefined) => {
		if (value === null || value === undefined || value === "") return "—";
		const num = Number(value);
		return Number.isFinite(num)
			? new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(
					num,
				)
			: String(value);
	};

	return (
		<Box sx={{ mt: compact ? 0 : 2 }}>
			<Paper
				variant="outlined"
				sx={{
					borderRadius: 3,
					borderColor: "divider",
					overflow: "hidden",
				}}
			>
				{/* Header */}
				<Box
					sx={(t) => ({
						px: 2.25,
						py: 1,
						borderBottom: 1,
						borderColor: alpha(t.palette.divider, 0.85),
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
						bgcolor:
							t.palette.mode === "dark"
								? alpha(t.palette.common.white, 0.02)
								: "grey.50",
					})}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								mb: 0.05,
								color: "primary.main",
								fontWeight: 700,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
							}}
						>
							<Translate String="Training.Drill.KpiParameters.Overline">
								Configuration
							</Translate>
						</Typography>
						<Typography variant="subtitle1" fontWeight={700}>
							<Translate String="Training.Drill.KpiParameters.Title">
								KPI Parameters
							</Translate>
						</Typography>
					</Box>
					{kpis?.length ? (
						<Chip
							size="small"
							label={`${kpis.length} parameter${kpis.length === 1 ? "" : "s"}`}
							sx={{
								height: 26,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								fontWeight: 700,
							}}
						/>
					) : null}
				</Box>

				{/* Empty state */}
				{!kpis?.length && (
					<Box
						sx={{
							px: 2,
							py: 3,
							display: "flex",
							alignItems: "center",
							gap: 2,
							bgcolor: (t) =>
								alpha(
									t.palette.primary.main,
									t.palette.mode === "dark" ? 0.04 : 0.02,
								),
						}}
					>
						<InfoOutlinedIcon sx={{ fontSize: 28, color: "text.disabled" }} />
						<Typography variant="body2" color="text.secondary">
							<Translate String="Training.Drill.KpiParameters.Empty">
								No KPI parameters available.
							</Translate>
						</Typography>
					</Box>
				)}

				{/* KPI list */}
				{kpis?.length ? (
					<List disablePadding>
						{kpis.map((kpi, idx) => {
							const kpiRecord = kpi as Record<string, unknown>;
							const unit =
								(typeof kpiRecord["Unit"] === "string"
									? kpiRecord["Unit"]
									: typeof kpiRecord["UnitName"] === "string"
										? kpiRecord["UnitName"]
										: "") ?? "";
							const description =
								typeof kpiRecord["Description"] === "string"
									? kpiRecord["Description"]
									: undefined;
							const recommendedRaw = kpiRecord["RecommendedValue"];
							const recommended =
								typeof recommendedRaw === "number" ||
								typeof recommendedRaw === "string"
									? recommendedRaw
									: undefined;

							return (
								<ListItem
									key={kpi.Id}
									disablePadding
									disableGutters
									sx={{
										borderBottom: idx < kpis.length - 1 ? 1 : 0,
										borderColor: "divider",
									}}
								>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 1,
											width: "100%",
											px: 2,
											py: 1.5,
											transition: "background-color 0.15s ease",
											"&:hover": {
												bgcolor: alpha(theme.palette.primary.main, 0.04),
											},
										}}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "flex-start",
												justifyContent: "space-between",
												gap: 2,
												flexWrap: "wrap",
											}}
										>
											<Box sx={{ minWidth: 0, flex: 1 }}>
												<Typography
													variant="body2"
													fontWeight={700}
													sx={{ lineHeight: 1.3 }}
													title={kpi.Name}
												>
													{kpi.Name}
												</Typography>
												{description && (
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ mt: 0.25, display: "block" }}
													>
														{description}
													</Typography>
												)}
											</Box>

											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													flexShrink: 0,
												}}
											>
												<Tooltip
													title={
														<>
															<Translate String="Training.Drill.KpiParameters.MaxValue">
																Max Value
															</Translate>
															:{" "}
															{unit
																? `${formatNumber(kpi.MaxValue)} ${unit}`
																: formatNumber(kpi.MaxValue)}
														</>
													}
													enterDelay={400}
												>
													<Chip
														size="small"
														color="primary"
														variant="outlined"
														icon={
															<TrendingUpRoundedIcon sx={{ fontSize: 14 }} />
														}
														label={
															<Typography variant="caption" fontWeight={700}>
																{formatNumber(kpi.MaxValue)}
																{unit ? ` ${unit}` : ""}
															</Typography>
														}
														sx={{
															height: 26,
															borderRadius: 2,
															"& .MuiChip-icon": { ml: 0.5, mr: 0 },
														}}
													/>
												</Tooltip>
												{recommended !== undefined && recommended !== null && (
													<Tooltip
														title={
															<Translate String="Training.Drill.KpiParameters.Recommended">
																Recommended
															</Translate>
														}
														enterDelay={400}
													>
														<Chip
															size="small"
															variant="outlined"
															label={
																<Typography variant="caption" fontWeight={600}>
																	{formatNumber(recommended)}
																	{unit ? ` ${unit}` : ""}
																</Typography>
															}
															sx={{
																height: 26,
																borderRadius: 2,
																borderColor: "divider",
																color: "text.secondary",
															}}
														/>
													</Tooltip>
												)}
											</Box>
										</Box>
									</Box>
								</ListItem>
							);
						})}
					</List>
				) : null}
			</Paper>
		</Box>
	);
};
